import * as React from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { markdownSanitizeSchema } from "./sanitizeSchema";
import { createCustomRenderers } from "./customRenderers";
import { splitAtArtefactLinks } from "./artefactSegments";
import { InlineArtefactViewer } from "@/components/feature/artefactsViewer/InlineArtefactViewer";
import type { Artefact } from "@/modules/chat/chat-ui.types";

const urlTransform = (url: string): string => {
  if (url.startsWith("artefact://")) return url;
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("mailto:")) return url;
  if (url.startsWith("/") || url.startsWith("#") || url.startsWith("./") || url.startsWith("../")) return url;
  return "";
};

interface Props {
  content: string;
  artefacts: Artefact[];
}

export const StreamingArtefactMarkdown: React.FC<Props> = ({ content, artefacts }) => {
  const segments = React.useMemo(() => splitAtArtefactLinks(content), [content]);

  const renderers = React.useMemo(() => createCustomRenderers({ evidences: [] }), []);

  return (
    <div className="markdown-content relative space-y-4 break-normal">
      {segments.map(segment => {
        if (segment.type === "artefact") {
          const artefact = artefacts.find(a => a.id === segment.artefactId);
          if (!artefact) return null;
          return <InlineArtefactViewer key={segment.key} artefact={artefact} />;
        }

        return (
          <ReactMarkdown
            key={segment.key}
            components={renderers}
            remarkPlugins={[remarkGfm, remarkBreaks]}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]]}
            urlTransform={urlTransform}
          >
            {segment.text}
          </ReactMarkdown>
        );
      })}
    </div>
  );
};
